import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import SearchableSelect from '../../components/common/SearchableSelect';
import { today } from '../../utils/format';

const emptyArticle = () => ({ article_no: '', name: '', unit: '', quantity: '', remarks: '' });

export default function GatepassForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [parties, setParties] = useState([]);
  const [units, setUnits] = useState([]);
  const [articleOptions, setArticleOptions] = useState([]);
  const [nextNo, setNextNo] = useState('');
  const [form, setForm] = useState({
    type: 'IN', copy_type: 'original', date: today(), party_source: 'party', party_id: '', other_party_name: '', is_returnable: false, person_name: '',
    vehicle_no: '', receiver_name: '', transporter: '', notes: '', articles: [emptyArticle()],
  });

  useEffect(() => {
    if (isEdit) return;
    api.gatepasses.nextNo()
      .then(({ data }) => setNextNo(data.gatepass_no || ''))
      .catch(() => setNextNo(''));
  }, [isEdit]);

  useEffect(() => {
    api.parties.list({ per_page: 1000 }).then(({ data }) => {
      const raw = data?.parties?.data || data?.data || data || [];
      setParties(Array.isArray(raw) ? raw : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.inventory.units().then(({ data }) => {
      const list = Array.isArray(data) ? data : data.units || [];
      setUnits(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.gatepasses.articles({ per_page: 1000 }).then(({ data }) => {
      const raw = data.articles || data || [];
      const list = Array.isArray(raw) ? raw : [];
      setArticleOptions(list.map((a) => ({
        value: a.article_no,
        label: a.name ? `${a.article_no} — ${a.name}` : a.article_no,
        article_no: a.article_no,
        name: a.name || '',
        unit: a.unit || '',
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.gatepasses.get(id).then(({ data }) => {
      const g = data.gatepass || data;
      setForm({
        type: g.type || 'IN',
        copy_type: g.copy_type || 'original',
        date: g.date || today(),
        party_source: g.party_id ? 'party' : 'other',
        party_id: g.party?.id || g.party_id || '',
        other_party_name: g.other_party_name || '',
        is_returnable: g.is_returnable === 1 || g.is_returnable === true,
        person_name: g.person_name || '',
        vehicle_no: g.vehicle_no || '',
        receiver_name: g.receiver_name || '',
        transporter: g.transporter || '',
        notes: g.notes || '',
        articles: (g.items && g.items.length)
          ? g.items.map((a) => ({ article_no: a.article_no || '', name: a.name || '', unit: a.unit || 'kg', quantity: a.quantity || '', remarks: a.remarks || '' }))
          : [emptyArticle()],
      });
    }).catch((err) => {
      showError('Failed to load gatepass', extractError(err));
      navigate('/gatepasses');
    }).finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const updateArticle = (i, field, value) => {
    setForm((p) => {
      const next = [...p.articles];
      next[i] = { ...next[i], [field]: value };
      return { ...p, articles: next };
    });
    if (errors.articles) setErrors((pr) => ({ ...pr, articles: '' }));
  };

  const updateArticleBulk = (i, fields) => {
    setForm((p) => {
      const next = [...p.articles];
      next[i] = { ...next[i], ...fields };
      return { ...p, articles: next };
    });
    if (errors.articles) setErrors((pr) => ({ ...pr, articles: '' }));
  };

  const addArticle = () => setForm((p) => ({ ...p, articles: [...p.articles, emptyArticle()] }));

  const removeArticle = (i) => setForm((p) => ({ ...p, articles: p.articles.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    const errs = {};
    if (!form.type) errs.type = 'Type is required';
    if (!form.date) errs.date = 'Date is required';
    if (form.party_source === 'party' && !form.party_id) errs.party_id = 'Party is required';
    if (form.party_source === 'other' && !form.other_party_name) errs.other_party_name = 'Party name is required';
    if (!form.person_name) errs.person_name = 'Person name is required';
    if (!form.vehicle_no) errs.vehicle_no = 'Vehicle no is required';
    if (!form.receiver_name) errs.receiver_name = 'Receiver name is required';
    if (!form.transporter) errs.transporter = 'Transporter is required';
    if (!form.notes) errs.notes = 'Notes are required';
    if (!form.articles.length) errs.articles = 'At least one article is required';
    else if (form.articles.some((a) => (form.party_source !== 'other' && !a.article_no) || !a.name || !a.unit || !a.quantity)) errs.articles = form.party_source === 'other' ? 'Each article needs name, unit and quantity' : 'Each article needs article_no, name, unit and quantity';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const validArticles = form.articles.filter((a) => (form.party_source === 'other' || a.article_no) && a.name && a.unit && a.quantity);
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        copy_type: form.copy_type || 'original',
        date: form.date,
        party_id: form.party_source === 'party' ? Number(form.party_id) : null,
        other_party_name: form.party_source === 'other' ? form.other_party_name : null,
        is_returnable: form.is_returnable,
        person_name: form.person_name,
        vehicle_no: form.vehicle_no,
        receiver_name: form.receiver_name,
        transporter: form.transporter,
        notes: form.notes,
        articles: validArticles.map((a) => ({
          article_no: a.article_no,
          name: a.name,
          unit: a.unit,
          quantity: Number(a.quantity),
          remarks: a.remarks || undefined,
        })),
      };
      if (isEdit) {
        await api.gatepasses.update(id, payload);
        toast('success', '', 'Gatepass updated');
      } else {
        await api.gatepasses.create(payload);
        toast('success', '', 'Gatepass created');
      }
      navigate('/gatepasses');
    } catch (err) {
      showError(isEdit ? 'Failed to update gatepass' : 'Failed to create gatepass', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader text="Loading gatepass..." />;

  const partyOptions = parties.map((p) => ({ value: p.id, label: p.name }));

  return (
    <div className="page">
      <PageHeader
        title={isEdit ? `Edit ${form.type || ''} Gatepass` : 'New Gatepass'}
        subtitle={isEdit ? `Editing gatepass — ${form.party_source === 'party' ? (parties.find(p => p.id === Number(form.party_id))?.name || '') : form.other_party_name}` : 'Create a material in/out gatepass.'}
        crumbs={[{ label: 'Inventory', to: '/gatepasses' }, { label: isEdit ? 'Edit Gatepass' : 'New Gatepass' }]}
        actions={
          <button className="btn btn-secondary" onClick={() => navigate('/gatepasses')}><FiArrowLeft /> Back to Gatepasses</button>
        }
      />

      <div className="card">
        <div className="card-body">
          {!isEdit && nextNo && (
            <div style={{ marginBottom: 14, fontSize: 13, color: 'var(--text-muted)' }}>
              Next Gatepass No: <b style={{ color: 'var(--primary)', fontSize: 14 }}>{nextNo}</b>
            </div>
          )}
          <div className="grid-3">
            <div className="form-group"><label className="form-label">Type <span className="req">*</span></label>
              <SearchableSelect options={[{ value: 'IN', label: 'Material In' }, { value: 'OUT', label: 'Material Out' }]} value={form.type} onChange={(v) => { set('type', v); if (errors.type) setErrors((p) => ({ ...p, type: '' })); }} isInvalid={!!errors.type} placeholder="-- Select --" />
              {errors.type && <div className="invalid-feedback">{errors.type}</div>}</div>
            <div className="form-group"><label className="form-label">Copy <span className="req">*</span></label>
              <div style={{ display: 'flex', gap: 16, paddingTop: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="copy_type" value="original" checked={form.copy_type === 'original'} onChange={() => set('copy_type', 'original')} />
                  Original
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="copy_type" value="duplicate" checked={form.copy_type === 'duplicate'} onChange={() => set('copy_type', 'duplicate')} />
                  Duplicate
                </label>
              </div></div>
            <div className="form-group"><label className="form-label">Date <span className="req">*</span></label>
              <input className={`form-control ${errors.date ? 'is-invalid' : ''}`} type="date" value={form.date} onChange={(e) => { set('date', e.target.value); if (errors.date) setErrors((p) => ({ ...p, date: '' })); }} />
              {errors.date && <div className="invalid-feedback">{errors.date}</div>}</div>
            <div className="form-group"><label className="form-label">Company <span className="req">*</span></label>
              <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="party_source" value="party" checked={form.party_source === 'party'} onChange={(e) => { set('party_source', e.target.value); set('party_id', ''); set('other_party_name', ''); if (errors.party_id || errors.other_party_name) setErrors((p) => ({ ...p, party_id: '', other_party_name: '' })); }} />
                  Party
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="party_source" value="other" checked={form.party_source === 'other'} onChange={(e) => { set('party_source', e.target.value); set('party_id', ''); set('other_party_name', ''); if (errors.party_id || errors.other_party_name) setErrors((p) => ({ ...p, party_id: '', other_party_name: '' })); }} />
                  Other
                </label>
              </div>
              {form.party_source === 'party' ? (
                <SearchableSelect options={partyOptions} value={form.party_id} onChange={(v) => { set('party_id', v); if (errors.party_id) setErrors((p) => ({ ...p, party_id: '' })); }} isInvalid={!!errors.party_id} placeholder="-- Select Party --" />
              ) : (
                <input className={`form-control ${errors.other_party_name ? 'is-invalid' : ''}`} value={form.other_party_name} onChange={(e) => { set('other_party_name', e.target.value); if (errors.other_party_name) setErrors((p) => ({ ...p, other_party_name: '' })); }} placeholder="Enter party name" />
              )}
              {errors.party_id && <div className="invalid-feedback">{errors.party_id}</div>}
              {errors.other_party_name && <div className="invalid-feedback">{errors.other_party_name}</div>}</div>
            <div className="form-group"><label className="form-label">Vehicle No <span className="req">*</span></label>
              <input className={`form-control ${errors.vehicle_no ? 'is-invalid' : ''}`} value={form.vehicle_no} onChange={(e) => { set('vehicle_no', e.target.value); if (errors.vehicle_no) setErrors((p) => ({ ...p, vehicle_no: '' })); }} placeholder="ABC-123" />
              {errors.vehicle_no && <div className="invalid-feedback">{errors.vehicle_no}</div>}</div>
            <div className="form-group"><label className="form-label">Person Name <span className="req">*</span></label>
              <input className={`form-control ${errors.person_name ? 'is-invalid' : ''}`} value={form.person_name} onChange={(e) => { set('person_name', e.target.value); if (errors.person_name) setErrors((p) => ({ ...p, person_name: '' })); }} />
              {errors.person_name && <div className="invalid-feedback">{errors.person_name}</div>}</div>
            <div className="form-group"><label className="form-label">Receiver Name <span className="req">*</span></label>
              <input className={`form-control ${errors.receiver_name ? 'is-invalid' : ''}`} value={form.receiver_name} onChange={(e) => { set('receiver_name', e.target.value); if (errors.receiver_name) setErrors((p) => ({ ...p, receiver_name: '' })); }} />
              {errors.receiver_name && <div className="invalid-feedback">{errors.receiver_name}</div>}</div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label className="form-label">Transporter <span className="req">*</span></label>
              <input className={`form-control ${errors.transporter ? 'is-invalid' : ''}`} value={form.transporter} onChange={(e) => { set('transporter', e.target.value); if (errors.transporter) setErrors((p) => ({ ...p, transporter: '' })); }} />
              {errors.transporter && <div className="invalid-feedback">{errors.transporter}</div>}</div>
            <div className="form-group"><label className="form-label">Returnable <span className="req">*</span></label>
              <div style={{ display: 'flex', gap: 16, paddingTop: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="is_returnable" value="true" checked={form.is_returnable === true} onChange={() => set('is_returnable', true)} />
                  Returnable
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="is_returnable" value="false" checked={form.is_returnable === false} onChange={() => set('is_returnable', false)} />
                  Non-Returnable
                </label>
              </div></div>
            <div className="form-group"><label className="form-label">Notes <span className="req">*</span></label>
              <textarea className={`form-control ${errors.notes ? 'is-invalid' : ''}`} value={form.notes} onChange={(e) => { set('notes', e.target.value); if (errors.notes) setErrors((p) => ({ ...p, notes: '' })); }} />
              {errors.notes && <div className="invalid-feedback">{errors.notes}</div>}</div>
          </div>

          <div className="section-title">Articles</div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr>{form.party_source !== 'other' && <th>Article No</th>}<th>Name</th><th style={{ width: 110 }}>Unit</th><th style={{ width: 100 }}>Quantity</th><th>Remarks</th><th style={{ width: 40 }}></th></tr></thead>
              <tbody>
                {form.articles.map((a, i) => (
                  <tr key={i}>
                    {form.party_source !== 'other' && (
                    <td>
                      {form.type === 'OUT' ? (
                        <SearchableSelect
                          options={articleOptions}
                          value={a.article_no}
                          onChange={(v) => {
                            const opt = articleOptions.find((o) => o.value === v);
                            updateArticleBulk(i, {
                              article_no: v,
                              name: opt?.name || '',
                              unit: opt?.unit || '',
                            });
                          }}
                          placeholder="-- Select article --"
                        />
                      ) : (
                        <input className={`form-control ${errors.articles ? 'is-invalid' : ''}`} value={a.article_no} onChange={(e) => updateArticle(i, 'article_no', e.target.value)} placeholder="ART-001" />
                      )}
                    </td>
                    )}
                    <td>
                      {form.type === 'OUT' && form.party_source !== 'other' && a.article_no ? (
                        <input className="form-control" value={a.name} readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed' }} />
                      ) : (
                        <input className={`form-control ${errors.articles ? 'is-invalid' : ''}`} value={a.name} onChange={(e) => updateArticle(i, 'name', e.target.value)} placeholder="Cotton Thread White" />
                      )}
                    </td>
                    <td>
                      {form.type === 'OUT' && form.party_source !== 'other' && a.article_no ? (
                        <input className="form-control" value={a.unit} readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed' }} />
                      ) : (
                        <select className="form-select" value={a.unit} onChange={(e) => updateArticle(i, 'unit', e.target.value)}>
                          <option value="">-- Select --</option>
                          {units.map((u) => <option key={u.id} value={u.short_name || u.name}>{u.short_name || u.name}</option>)}
                        </select>
                      )}
                    </td>
                    <td><input className={`form-control ${errors.articles ? 'is-invalid' : ''}`} type="number" value={a.quantity} onChange={(e) => updateArticle(i, 'quantity', e.target.value)} placeholder="0" /></td>
                    <td><input className="form-control" value={a.remarks} onChange={(e) => updateArticle(i, 'remarks', e.target.value)} /></td>
                    <td><button className="btn-icon danger" onClick={() => removeArticle(i)}><FiTrash2 /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {errors.articles && <div className="invalid-feedback">{errors.articles}</div>}
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 10 }} onClick={addArticle}>
            <FiPlus /> Add Article
          </button>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/gatepasses')}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update Gatepass' : 'Create Gatepass'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}